import type {
  DpoInfoAudioKey,
  LogoPosition,
  PurposePreselection,
  SelectionControlType,
} from "./api/types";
import type { NoticeFontEntry } from "./types";
import { NOTICE_CHROME_EN } from "./modal/chromeCopy.data";

/**
 * The production consent API. Every default in this package resolves here, so
 * no entry point can ship a host of its own -- the public calls used to fall
 * back to a staging constant and wrote consent records there.
 */
export const DEFAULT_CONSENT_API_BASE_URL = "https://api.redacto.io/consent";

export const DEFAULT_LOGO_POSITION: LogoPosition = "left";

/**
 * English fallbacks for the action buttons, used when the notice config omits
 * a label — an older consent-server, or a notice published before the
 * accept-all shortcut existed.
 */
export const DEFAULT_ACCEPT_ALL_BUTTON_TEXT = "Accept All";
export const DEFAULT_ACCEPT_SELECTED_BUTTON_TEXT = "Accept Selected";
export const DEFAULT_DECLINE_BUTTON_TEXT = "Decline";

/**
 * DOM ids for the footer buttons. `confirm-button-text` and
 * `decline-button-text` predate the accept-all shortcut and are load-bearing
 * for the e2e suite and the TTS highlight sequence — do not rename them.
 */
export const ACCEPT_ALL_BUTTON_ID = "accept-all-button-text";
export const ACCEPT_SELECTED_BUTTON_ID = "confirm-button-text";
export const DECLINE_BUTTON_ID = "decline-button-text";

export const SUBMITTING_BUTTON_TEXT = "Submitting...";

/**
 * `justify-content` for the logo row, keyed by the configured position. The row
 * is full width, so aligning it is the whole of the layout change.
 */
export const LOGO_POSITION_JUSTIFY: Record<LogoPosition, string> = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
};

/** Glyph marking a required data element, and the purpose that carries one. */
export const REQUIRED_MARKER_GLYPH = "*";

export const REQUIRED_MARKER_ARIA_LABEL = "required";

const REQUIRED_MARKER_COLOR = "red";

/** Gap between the label the marker annotates and the marker itself. */
const REQUIRED_MARKER_GAP = "4px";

export const REQUIRED_MARKER_STYLE: Readonly<Record<string, string>> = {
  color: REQUIRED_MARKER_COLOR,
  marginLeft: REQUIRED_MARKER_GAP,
};

export const DPO_AUDIO_ELEMENT_MAP: ReadonlyArray<{
  audioKey: DpoInfoAudioKey;
  elementId: string;
}> = [
  { audioKey: "grievance_text_audio_url", elementId: "dpo-grievance-text" },
  { audioKey: "grievance_anchor_text_audio_url", elementId: "dpo-grievance-link" },
  { audioKey: "grievance_email_connector_text_audio_url", elementId: "dpo-grievance-email-connector" },
  { audioKey: "grievance_email_audio_url", elementId: "dpo-grievance-email" },
  { audioKey: "dp_board_text_audio_url", elementId: "dpo-dp-board-text" },
  { audioKey: "dp_board_anchor_text_audio_url", elementId: "dpo-dp-board-link" },
  { audioKey: "dpo_text_audio_url", elementId: "dpo-dpo-text" },
  { audioKey: "dpo_anchor_text_audio_url", elementId: "dpo-dpo-link" },
] as const;

export const PURPOSE_STATUS_ACTIVE = "ACTIVE";

export const PRODUCT_SECTION_COPY = {
  toggleSelection: (productName: string, purposeCount: number) =>
    `Toggle all ${purposeCount} purposes for ${productName}`,
  toggleRequired: (productName: string, purposeCount: number) =>
    `Toggle the ${purposeCount} required purposes for ${productName}`,
  settled: (productName: string) => `Already consented to ${productName}`,
} as const;

export const CONFIRM_HINT_ID = "redacto-confirm-hint";

/**
 * Why "Accept Selected" is disabled, in English. The notice reads the
 * translated strings from `noticeChromeCopy`; these are the defaults.
 */
export const CONFIRM_HINT_COPY = {
  "nothing-chosen": NOTICE_CHROME_EN.hintNothingChosen,
  "nothing-engaged": NOTICE_CHROME_EN.hintNothingEngaged,
  "required-outstanding": NOTICE_CHROME_EN.hintRequiredOutstanding,
  productsIncomplete: (productNames: string[]) =>
    `Accept the required purposes for ${productNames.join(", ")} to confirm.`,
} as const;

export const DEFAULT_PURPOSE_PRESELECTION: PurposePreselection = "NONE";

export const PURPOSE_STATUS_INACTIVE = "INACTIVE";

export const OTP_GATE_LENGTH = 6;

export const OTP_GATE_TITLE_ID = "notice-otp-gate-title";

export const OTP_GATE_COPY = {
  title: NOTICE_CHROME_EN.otpTitle,
  description: NOTICE_CHROME_EN.otpDescription,
  submit: NOTICE_CHROME_EN.otpSubmit,
  verifying: NOTICE_CHROME_EN.otpVerifying,
  failed: NOTICE_CHROME_EN.otpFailed,
} as const;

export const PRODUCT_POLICIES_LABEL = NOTICE_CHROME_EN.productPoliciesLabel;

export const DEFAULT_SELECTION_CONTROL: SelectionControlType = "checkbox";

export const SELECTION_CONTROL_ACCENT = "#4f87ff";

export const SELECTION_CONTROL_COPY = {
  onRecord: (answer: string) =>
    NOTICE_CHROME_EN.savedAnswer.replace("{answer}", answer),
  mixed: NOTICE_CHROME_EN.mixed,
  options: [
    { label: NOTICE_CHROME_EN.yes, value: true },
    { label: NOTICE_CHROME_EN.no, value: false },
  ],
} as const;

export const CONFIRM_DIALOG_ID = "redacto-confirm-dialog";
export const CONFIRM_DIALOG_DETAIL_ID = "redacto-confirm-dialog-detail";

export const CONFIRM_DIALOG_COPY = {
  "accept-all": {
    title: NOTICE_CHROME_EN.confirmAcceptAllTitle,
    detail: NOTICE_CHROME_EN.confirmAcceptAllDetail,
  },
  "accept-selected": {
    title: NOTICE_CHROME_EN.confirmAcceptSelectedTitle,
    detail: NOTICE_CHROME_EN.confirmAcceptSelectedDetail,
  },
  decline: {
    title: NOTICE_CHROME_EN.confirmDeclineTitle,
    detail: NOTICE_CHROME_EN.confirmDeclineDetail,
  },
  confirm: NOTICE_CHROME_EN.yes,
  cancel: NOTICE_CHROME_EN.no,
} as const;

export const INHERIT_FONT_FAMILY = "inherit";

export const DEFAULT_NOTICE_FONT_ID = "arial";

export const NOTICE_FONT_GENERIC_FALLBACK = ", sans-serif";

export const NOTICE_FONT_CATALOGUE: ReadonlyArray<NoticeFontEntry> = [
  {
    id: "montserrat",
    value: "Montserrat",
    stack: 'Montserrat, "Trebuchet MS", "Segoe UI", Arial, sans-serif',
  },
  { id: "arial", value: "Arial", stack: "Arial, Helvetica, sans-serif" },
  {
    id: "georgia",
    value: "Georgia",
    stack: 'Georgia, "Times New Roman", Times, serif',
  },
  {
    id: "times new roman",
    value: "Times New Roman",
    stack: '"Times New Roman", Times, serif',
  },
  {
    id: "trebuchet ms",
    value: "Trebuchet MS",
    stack: '"Trebuchet MS", "Lucida Sans Unicode", "Lucida Grande", sans-serif',
  },
  { id: "verdana", value: "Verdana", stack: "Verdana, Geneva, sans-serif" },
];

export const CSS_FONT_KEYWORDS: ReadonlyArray<string> = [
  "inherit",
  "initial",
  "revert",
  "revert-layer",
  "unset",
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-serif",
  "ui-sans-serif",
  "ui-monospace",
  "ui-rounded",
  "math",
  "emoji",
  "fangsong",
];

export const UNSAFE_FONT_CHARACTERS = /["';{}\\]/g;

export const NOTICE_FONT_ROOT_ATTRIBUTE = "data-redacto-notice-font";

export const NOTICE_FONT_STYLE_ID = "redacto-notice-font-styles";

export const NOTICE_FONT_CSS = `
  [${NOTICE_FONT_ROOT_ATTRIBUTE}] button,
  [${NOTICE_FONT_ROOT_ATTRIBUTE}] input,
  [${NOTICE_FONT_ROOT_ATTRIBUTE}] optgroup,
  [${NOTICE_FONT_ROOT_ATTRIBUTE}] option,
  [${NOTICE_FONT_ROOT_ATTRIBUTE}] select,
  [${NOTICE_FONT_ROOT_ATTRIBUTE}] textarea {
    font-family: inherit;
  }
`;
